import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, newKey } from '../api/client';
import { useInHouse, useMyShift, type Tender } from '../api/restaurant';
import { useSvcMutation } from '../api/services';
import { formatGhs, parseCedis } from '../format';
import { Button, Field, Loading, Modal, useReport, useToast } from '../ui/kit';

const TENDERS: { id: Tender; label: string }[] = [
  { id: 'CASH', label: 'Cash' },
  { id: 'MOMO', label: 'MoMo' },
  { id: 'CARD', label: 'Card' },
  { id: 'ROOM', label: 'Room' },
];

/**
 * Takes payment for one pool booking or one trip: cash, MoMo or card at the desk, or the guest's room.
 * The whole amount is taken in one go. A fresh Idempotency-Key is made per attempt and kept until it succeeds,
 * so a dropped connection followed by a second tap cannot take the money twice.
 */
export function ServicePayDialog({ title, payPath, total, bookingId, onClose }: { title: string; payPath: string; total: number; bookingId: string | null; onClose: () => void }) {
  const shift = useMyShift(true);
  const toast = useToast();
  const report = useReport();
  const [tender, setTender] = useState<Tender>(bookingId ? 'ROOM' : 'CASH');
  const [reference, setReference] = useState('');
  const [received, setReceived] = useState('');
  const [guestSearch, setGuestSearch] = useState('');
  const [guest, setGuest] = useState<string | null>(bookingId);
  const key = useRef(newKey());
  const guests = useInHouse(guestSearch.trim(), tender === 'ROOM' && !guest);

  const pay = useSvcMutation((body: Record<string, unknown>) => api(payPath, { method: 'POST', body, idempotencyKey: key.current }));
  const refNeeded = tender === 'MOMO' || tender === 'CARD';
  const needShift = tender !== 'ROOM' && !shift.isLoading && !shift.data;
  const recv = Number(received);
  const change = tender === 'CASH' && received.trim() !== '' && Number.isFinite(recv) ? Math.round(recv * 100) - total : 0;
  const valid = !needShift && (!refNeeded || reference.trim().length >= 4) && (tender !== 'ROOM' || !!guest);

  return (
    <Modal open onClose={onClose} title={title}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          pay.mutate(
            { tender, ...(refNeeded ? { reference: reference.trim() } : {}), ...(tender === 'ROOM' && guest ? { bookingId: guest } : {}) },
            {
              onError: report,
              onSuccess: () => {
                key.current = newKey();
                toast.ok(change > 0 ? `Paid. Give ${formatGhs(change)} change.` : 'Paid');
                onClose();
              },
            },
          );
        }}
      >
        <div className="total-row">
          <span>To pay</span>
          <span className="amount num">{formatGhs(total)}</span>
        </div>
        <div className="seg" role="group" aria-label="How is it paid?">
          {TENDERS.map((t) => (
            <button key={t.id} type="button" aria-pressed={tender === t.id} onClick={() => setTender(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {needShift ? (
          <div className="banner">
            Open your till session first. <Link to="/till" onClick={onClose}>Go to the till</Link>
          </div>
        ) : null}

        {tender === 'ROOM' ? (
          guest ? (
            <div className="banner info">
              Charging this to the guest’s room.{' '}
              {bookingId ? null : (
                <button type="button" className="linklike" onClick={() => setGuest(null)}>
                  Choose someone else
                </button>
              )}
            </div>
          ) : (
            <div className="stack">
              <Field id="sp-guest" label="Room number or guest name">
                <input id="sp-guest" className="input" value={guestSearch} onChange={(e) => setGuestSearch(e.target.value)} autoComplete="off" autoFocus />
              </Field>
              {guests.isLoading ? (
                <Loading rows={1} />
              ) : (
                <div className="list">
                  {(guests.data ?? []).map((g) => (
                    <button key={g.bookingId} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setGuest(g.bookingId)}>
                      <span className="title">
                        Room {g.room ?? '?'} · {g.guest}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        ) : tender === 'CASH' ? (
          <Field id="sp-recv" label="Cash received (optional)" hint={change > 0 ? `Change to give: ${formatGhs(change)}` : 'To work out the change'}>
            <input id="sp-recv" className="input" inputMode="decimal" value={received} onChange={(e) => setReceived(e.target.value)} />
          </Field>
        ) : (
          <Field id="sp-ref" label={tender === 'MOMO' ? 'MoMo transaction ID' : 'Card slip number'} hint={tender === 'MOMO' ? 'From the payment confirmation message. Check the money has arrived first.' : 'From the card terminal slip. Never type a card number.'}>
            <input id="sp-ref" className="input" value={reference} onChange={(e) => setReference(e.target.value)} autoCapitalize="characters" />
          </Field>
        )}

        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={pay.isPending}>
            {tender === 'ROOM' ? 'Charge to room' : `Take ${formatGhs(total)}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** "GH₵30.00  Change" that turns into a small form. Used for ticket and trip prices. */
export function InlinePrice({ value, onSave, pending }: { value: number; onSave: (pesewas: number) => void; pending: boolean }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const parsed = parseCedis(text);
  if (!editing) {
    return (
      <span className="row">
        <span className="num">{formatGhs(value)}</span>
        <button
          type="button"
          className="linklike"
          onClick={() => {
            setText((value / 100).toFixed(2));
            setEditing(true);
          }}
        >
          Change
        </button>
      </span>
    );
  }
  return (
    <form
      className="row"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (parsed !== null) {
          onSave(parsed);
          setEditing(false);
        }
      }}
    >
      <input className="input" aria-label="New price" inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} style={{ width: 110 }} autoFocus />
      <Button type="submit" disabled={parsed === null || parsed === value} loading={pending}>
        Save
      </Button>
      <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </form>
  );
}
